export const CLIENT_ADMIN_ROLES = ["Client Admin"] as const;

export const CLIENT_HOME_ROLES = [
  "Customer User",
  "Client Admin",
  "Client Accounting",
  "Client Operations",
  "Client Processors",
  "Diligent Search User",
  "State Reporter",
] as const;

/**
 * UserRoles.RoleId values that represent client-side users across environments
 * (aligns with Message Center sender attribution).
 */
export const CLIENT_PORTAL_ROLE_IDS: readonly number[] = [1, 6, 8, 9, 10, 11, 12, 13];

export function isClientPortalRoleId(roleId: number | string | undefined | null): boolean {
  if (roleId == null || roleId === "") return false;
  const id = typeof roleId === "number" ? roleId : Number(roleId);
  if (Number.isNaN(id)) return false;
  return CLIENT_PORTAL_ROLE_IDS.includes(id);
}

export const splitRoles = (userRoles?: string): string[] => {
  if (!userRoles) return [];
  return userRoles
    .split(",")
    .map((role) => role.trim())
    .filter(Boolean);
};

export const hasAnyRole = (
  userRoles?: string,
  allowedRoles: readonly string[] = []
): boolean => {
  const roles = splitRoles(userRoles);
  return roles.some((role) => allowedRoles.includes(role));
};

/** Client portal / home-area users (same list used for layout routing and document–message name masking). */
export const isClientHomeRole = (userRoles?: string): boolean => {
  return hasAnyRole(userRoles, CLIENT_HOME_ROLES);
};

/** Client portal: Non-Premium Endorsements page (upload + prior submissions). */
export const isClientAdmin = (userRoles?: string): boolean => {
  return hasAnyRole(userRoles, CLIENT_ADMIN_ROLES);
};

/** MISC filing queue: view and mark complete non-premium endorsements. */
export const MISC_NON_PREMIUM_VIEW_ROLES = [
  "Super Admin",
  "MISC Filer",
  "MISC State Reporter",
] as const;

export const MISC_NON_PREMIUM_COMPLETE_ROLES = MISC_NON_PREMIUM_VIEW_ROLES;

export const isMiscNonPremiumViewer = (userRoles?: string): boolean => {
  return hasAnyRole(userRoles, MISC_NON_PREMIUM_VIEW_ROLES);
};

export const canMarkMiscNonPremiumComplete = (userRoles?: string): boolean => {
  return hasAnyRole(userRoles, MISC_NON_PREMIUM_COMPLETE_ROLES);
};

export const isSuperAdminRole = (userRoles?: string): boolean => {
  return hasAnyRole(userRoles, ["Super Admin"]);
};

/**
 * Large Report Fee is Admin-only. State Reporter marks Large Report;
 * Super Admin updates the fee (Billing Edit / File Now).
 */
export const canEnterLargeReportFee = (userRoles?: string): boolean => {
  return isSuperAdminRole(userRoles);
};

export const MISC_PORTAL_ROLES = [
  "Super Admin",
  "MISC Filer",
  "MISC State Reporter",
] as const;

/** Roles allowed to open Monthly Account Performance Report (client portal + MISC). */
export const MONTHLY_ACCOUNT_PERFORMANCE_REPORT_ROLES = [
  ...CLIENT_HOME_ROLES,
  ...MISC_PORTAL_ROLES,
] as const;

/** Roles allowed to open the Quality Control Report (MISC Super Admin + MISC State Reporter).
 * Not the client-portal "State Reporter" role. */
export const QUALITY_CONTROL_REPORT_ROLES = [
  "Super Admin",
  "MISC State Reporter",
] as const;

/** Second MISC role. Grants the Open Tax Report page only. */
export const OTR_ACCESS_ROLE = "OTR Access";

/** Route access. MISC Filer already had the URL. MISC State Reporter does not. */
export const OPEN_TAX_REPORT_ROLES = [
  "Super Admin",
  "MISC Filer",
  OTR_ACCESS_ROLE,
] as const;

/** Menu link. Super Admin already sees it. OTR Access is the only added role. */
export const OPEN_TAX_REPORT_MENU_ROLES = [
  "Super Admin",
  OTR_ACCESS_ROLE,
] as const;

/** Approve, row selection, and the Remove menu (Remove and Offset). */
export const OPEN_TAX_REPORT_APPROVE_ROLES = OPEN_TAX_REPORT_MENU_ROLES;

export function canApproveOpenTaxReport(userRoles?: string | null): boolean {
  return hasAnyRole(userRoles ?? undefined, OPEN_TAX_REPORT_APPROVE_ROLES);
}

export function canRemoveOpenTaxReport(userRoles?: string | null): boolean {
  return canApproveOpenTaxReport(userRoles);
}

/** Header label only. Does not change the login role string used for permission checks. */
export function formatRoleLabel(userRoles?: string | null): string {
  return splitRoles(userRoles ?? undefined).join(", ");
}
